import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plane,
  Plus,
  Trash2,
  LogOut,
  TrendingDown,
  TrendingUp,
  Minus,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  Users,
  Calendar,
  MapPin,
  Settings as SettingsIcon,
  ChartLine,
  ChevronDown,
  Search,
  Home,
  Link,
  SlidersHorizontal,
} from "lucide-react";
import Settings from "./Settings";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const PRICE_CHECK_INTERVAL_MS = 30000;

// --- Utility functions ---

const formatPrice = (price) => {
  if (!price) return "\u2014";
  return new Intl.NumberFormat("fr-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

const getPriceChangeColor = (change) => {
  if (!change || change === 0) return "text-zinc-400";
  return change < 0 ? "text-emerald-400" : "text-red-400";
};

const getPriceChangeBadgeClass = (change) => {
  if (!change || change === 0) return "bg-zinc-800 text-zinc-400 border-zinc-700";
  return change < 0
    ? "bg-emerald-950/30 text-emerald-400 border-emerald-900/50"
    : "bg-red-950/30 text-red-400 border-red-900/50";
};

// --- Chart constants (extracted to avoid re-renders) ---

const CHART_MARGIN = { top: 5, right: 10, left: 10, bottom: 0 };
const CHART_AXIS_TICK = { fill: "rgba(255,255,255,0.35)", fontSize: 11 };
const CHART_AXIS_LINE = { stroke: "rgba(255,255,255,0.1)" };
const CHART_DOT = { r: 3, fill: "#3b82f6", strokeWidth: 0 };
const CHART_ACTIVE_DOT = { r: 5, fill: "#60a5fa", strokeWidth: 2, stroke: "#1e3a5f" };

// --- Sub-components ---

const PriceChangeIcon = ({ change }) => {
  if (!change || change === 0) return <Minus className="w-4 h-4" />;
  return change < 0 ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />;
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className="bg-[#1a1a1d] border border-white/10 rounded-lg px-3 py-2 text-sm shadow-xl">
      <p className="text-white font-mono font-semibold">{formatPrice(data.price)}</p>
      <p className="text-white/50 text-xs mt-0.5">{data.label}</p>
    </div>
  );
};

const PriceHistoryChart = ({ packageId, token, isOpen }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!isOpen || fetched) return;
    setLoading(true);
    axios
      .get(`${API}/packages/${packageId}/history`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        setHistory(res.data);
        setFetched(true);
      })
      .catch(() => toast.error("Impossible de charger l'historique"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and axios are stable module-level constants
  }, [isOpen, packageId, token, fetched]);

  if (!isOpen) return null;

  const chartData = history.map((entry) => {
    const d = new Date(entry.recorded_at);
    return {
      price: entry.price,
      label: d.toLocaleString("fr-CA", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }),
      ts: d.getTime(),
    };
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="text-center py-6 text-white/40 text-sm">
        Aucune donnee d'historique. Le prix sera enregistre a chaque verification.
      </div>
    );
  }

  const prices = chartData.map((d) => d.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const padding = Math.max((maxPrice - minPrice) * 0.15, 50);

  return (
    <div className="pt-4" data-testid={`price-history-chart-${packageId}`}>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={chartData} margin={CHART_MARGIN}>
          <defs>
            <linearGradient id={`grad-${packageId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis
            dataKey="label"
            tick={CHART_AXIS_TICK}
            axisLine={CHART_AXIS_LINE}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            domain={[minPrice - padding, maxPrice + padding]}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`}
            width={45}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="price"
            stroke="#3b82f6"
            strokeWidth={2}
            fill={`url(#grad-${packageId})`}
            dot={CHART_DOT}
            activeDot={CHART_ACTIVE_DOT}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const PackageCard = ({ pkg, index, refreshingId, onRefresh, onDelete, token }) => {
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <div
      className="bg-[#121214] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all duration-300 animate-fadeIn"
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`package-card-${pkg.id}`}
    >
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
      {/* Package Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            pkg.package_info.provider === 'transat' ? 'bg-blue-500/10' : 'bg-orange-500/10'
          }`}>
            <Plane className={`w-5 h-5 ${
              pkg.package_info.provider === 'transat' ? 'text-blue-400' : 'text-orange-400'
            }`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-lg font-semibold text-white truncate">
                {pkg.package_info.hotel_name}
              </h3>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                pkg.package_info.provider === 'transat' 
                  ? 'bg-blue-500/20 text-blue-300' 
                  : 'bg-orange-500/20 text-orange-300'
              }`}>
                {pkg.package_info.provider === 'transat' ? 'Transat' : 'Air Canada'}
              </span>
              {(pkg.tracking_status === 'unavailable' || pkg.tracking_status === 'expired' || pkg.tracking_status === 'stopped') && (
                <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${pkg.tracking_status === 'stopped' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`} data-testid={`tracking-stopped-badge-${pkg.id}`}>
                  <AlertCircle className="w-3 h-3" />
                  {pkg.tracking_status === 'expired' ? 'Date passée - suivi arrêté' : pkg.tracking_status === 'stopped' ? 'Erreur - suivi arrêté' : 'Complet - suivi arrêté'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-white/50">
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {pkg.package_info.departure_name?.split("(")[0]?.trim() || pkg.package_info.departure_code}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {pkg.package_info.departure_date} - {pkg.package_info.return_date}
                {pkg.package_info.duration_nights && ` (${pkg.package_info.duration_nights} nuits)`}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {pkg.package_info.adults} adultes
                {pkg.package_info.children > 0 && `, ${pkg.package_info.children} enfants`}
              </span>
            </div>
            
            {pkg.last_error && (
              <div className={`flex items-center gap-2 mt-2 text-sm ${(pkg.tracking_status === 'unavailable' || pkg.tracking_status === 'expired' || pkg.last_error.toLowerCase().includes("disponible")) ? "text-amber-400" : "text-red-400"}`} data-testid="package-error-message">
                <AlertCircle className="w-4 h-4" />
                {pkg.last_error === "Could not fetch price" ? "Impossible de recuperer le prix" : pkg.last_error}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Prices */}
      <div className="flex items-center gap-6 lg:gap-8">
        <div className="text-center">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Initial</p>
          <p className="font-mono text-lg text-white/60">
            {pkg.initial_price ? formatPrice(pkg.initial_price) : (
              <span className="text-white/30 animate-pulse-slow">Chargement...</span>
            )}
          </p>
        </div>

        <div className="text-center">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Actuel</p>
          <p className={`font-mono text-xl font-bold ${getPriceChangeColor(pkg.price_change)}`}>
            {pkg.current_price ? formatPrice(pkg.current_price) : (
              <span className="text-white/30 animate-pulse-slow">Chargement...</span>
            )}
          </p>
        </div>

        {pkg.price_change !== null && pkg.price_change !== 0 && (
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium border ${getPriceChangeBadgeClass(pkg.price_change)}`}>
            <PriceChangeIcon change={pkg.price_change} />
            <span className="font-mono">
              {pkg.price_change > 0 ? "+" : ""}
              {formatPrice(pkg.price_change)}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 lg:ml-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setHistoryOpen(!historyOpen)}
          className={`text-white/50 hover:text-white hover:bg-white/5 ${historyOpen ? "bg-white/5 text-white" : ""}`}
          data-testid={`history-button-${pkg.id}`}
        >
          <ChartLine className="w-4 h-4" />
          <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${historyOpen ? "rotate-180" : ""}`} />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRefresh(pkg)}
          disabled={refreshingId === pkg.id}
          className="text-white/50 hover:text-white hover:bg-white/5"
          data-testid={`refresh-button-${pkg.id}`}
        >
          <RefreshCw className={`w-4 h-4 ${refreshingId === pkg.id ? "animate-spin" : ""}`} />
        </Button>
        
        <a
          href={pkg.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-white/50 hover:text-white hover:bg-white/5 transition-colors"
          data-testid={`view-link-${pkg.id}`}
        >
          <ExternalLink className="w-4 h-4" />
        </a>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(pkg)}
          className="text-red-400/70 hover:text-red-400 hover:bg-red-950/30"
          data-testid={`delete-button-${pkg.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>

    {/* Price History Chart */}
    <PriceHistoryChart packageId={pkg.id} token={token} isOpen={historyOpen} />

    {/* Last checked */}
    {pkg.last_checked && (
      <p className="text-xs text-white/30 mt-4 text-right">
        Derniere verification: {new Date(pkg.last_checked).toLocaleString('fr-CA')}
      </p>
    )}
  </div>
  );
};

const HowToGuide = () => {
  const steps = [
    { icon: SlidersHorizontal, title: "Choisissez vos parametres", desc: "Lieu de depart, destination, dates, duree, voyageurs" },
    { icon: Search, title: "Lancez la recherche", desc: "Sur Air Canada Vacations ou Air Transat" },
    { icon: Home, title: "Selectionnez votre hotel", desc: "Choisissez l'hotel qui vous interesse" },
    { icon: Link, title: "Copiez le lien", desc: "Depuis la page de selection des chambres" },
  ];

  return (
    <div className="mt-12 animate-fadeIn" data-testid="how-to-guide">
      <div className="mb-6">
        <h3 className="font-heading text-xl font-semibold text-white">Comment ajouter un forfait?</h3>
        <p className="text-white/40 text-sm mt-1">Suivez ces etapes pour commencer le suivi d'un forfait vacances</p>
      </div>

      {/* Steps */}
      <div className="flex items-start justify-between mb-8 relative">
        <div className="absolute top-6 left-[60px] right-[60px] h-px bg-white/[0.06]" />
        {steps.map((step, i) => (
          <div key={i} className="flex-1 flex flex-col items-center text-center relative z-10">
            <div className="w-12 h-12 rounded-xl bg-[#18181B] border border-white/[0.08] flex items-center justify-center mb-3">
              <step.icon className="w-5 h-5 text-white/40" />
            </div>
            <p className="text-sm font-medium text-white/80">{step.title}</p>
            <p className="text-xs text-white/30 max-w-[160px] mt-1 leading-relaxed">{step.desc}</p>
          </div>
        ))}
      </div>

      {/* Provider Cards */}
      <div className="grid md:grid-cols-2 gap-5">
        {/* Air Canada */}
        <div className="bg-[#121214] border border-white/[0.08] rounded-2xl overflow-hidden">
          <div className="px-5 py-4 flex items-center gap-3 border-b border-white/[0.06]">
            <span className="w-2 h-2 rounded-full bg-orange-400" />
            <span className="font-semibold text-white">Air Canada Vacations</span>
            <a href="https://vacations.aircanada.com" target="_blank" rel="noopener noreferrer"
               className="ml-auto text-xs text-white/30 font-mono hover:text-white/50 transition-colors hover:underline">
              vacations.aircanada.com
            </a>
          </div>
          <div className="p-5">
            <div className="relative rounded-lg overflow-hidden border border-white/[0.08] mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_d13bf343-e79e-432e-8058-1bd16a0f8738/artifacts/cvkvmya8_AirCanada.png"
                alt="Air Canada - Etape Chambre(s)"
                className="w-full h-[200px] object-cover object-top opacity-80"
              />
              <div className="absolute top-0 left-0 right-0 h-[34px] border-[2.5px] border-red-500 rounded-sm" />
              <div className="absolute top-[38px] left-1/2 -translate-x-1/2 bg-red-500 text-white text-xs font-semibold px-3 py-1 rounded-md">
                Copiez cette URL
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-500/15 text-orange-300">Copiez</span>
              <p className="text-sm text-white/60">Copiez le lien a l'etape selection des <strong className="text-white">Chambres</strong></p>
            </div>
          </div>
        </div>

        {/* Air Transat */}
        <div className="bg-[#121214] border border-white/[0.08] rounded-2xl overflow-hidden">
          <div className="px-5 py-4 flex items-center gap-3 border-b border-white/[0.06]">
            <span className="w-2 h-2 rounded-full bg-blue-400" />
            <span className="font-semibold text-white">Air Transat</span>
            <a href="https://booking.transat.com" target="_blank" rel="noopener noreferrer"
               className="ml-auto text-xs text-white/30 font-mono hover:text-white/50 transition-colors hover:underline">
              booking.transat.com
            </a>
          </div>
          <div className="p-5">
            <div className="relative rounded-lg overflow-hidden border border-white/[0.08] mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_d13bf343-e79e-432e-8058-1bd16a0f8738/artifacts/b1h6ohb7_Transat.png"
                alt="Air Transat - Etape Chambre"
                className="w-full h-[200px] object-cover object-top opacity-80"
              />
              <div className="absolute top-0 left-0 right-0 h-[34px] border-[2.5px] border-red-500 rounded-sm" />
              <div className="absolute top-[38px] left-1/2 -translate-x-1/2 bg-red-500 text-white text-xs font-semibold px-3 py-1 rounded-md">
                Copiez cette URL
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-500/15 text-blue-300">Copiez</span>
              <p className="text-sm text-white/60">Copiez le lien a l'etape selection des <strong className="text-white">Chambres</strong></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddPackageModal = ({ open, onOpenChange, onSubmit, newUrl, setNewUrl, adding }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#121214] border-white/10 text-white sm:max-w-xl">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl">Ajouter un forfait a suivre</DialogTitle>
        <DialogDescription className="text-white/50">
          Collez l'URL d'un forfait vacances Air Canada ou Air Transat pour suivre son prix.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit}>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="url" className="text-white/80">
              URL du forfait
            </Label>
            <Input
              id="url"
              data-testid="package-url-input"
              type="url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://vacations.aircanada.com/... ou https://booking.transat.com/..."
              className="bg-[#09090B] border-white/10 focus:border-white/30 text-white placeholder:text-white/20 font-mono text-sm"
            />
          </div>
          
          <div className="bg-white/5 rounded-lg p-4 text-sm text-white/50">
            <p className="font-medium text-white/70 mb-2">Fournisseurs supportes:</p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-orange-400"></span>
                <span><strong className="text-white/70">Air Canada</strong> - vacations.aircanada.com</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                <span><strong className="text-white/70">Air Transat</strong> - booking.transat.com</span>
              </li>
            </ul>
            <p className="mt-3 text-xs">Recherchez votre forfait sur l'un des sites, puis copiez l'URL complete depuis votre navigateur.</p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-white/50 hover:text-white hover:bg-white/5"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={adding}
            className="bg-white text-black hover:bg-white/90 font-semibold"
            data-testid="confirm-add-package-button"
          >
            {adding ? (
              <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
            ) : (
              "Ajouter le forfait"
            )}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

const DeleteConfirmDialog = ({ open, onOpenChange, selectedPackage, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent className="bg-[#121214] border-white/10 text-white">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-heading">Retirer le forfait?</AlertDialogTitle>
        <AlertDialogDescription className="text-white/50">
          Ceci arretera le suivi des changements de prix pour{" "}
          <span className="text-white">{selectedPackage?.package_info?.hotel_name}</span>.
          Vous pourrez toujours le rajouter plus tard.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white">
          Annuler
        </AlertDialogCancel>
        <AlertDialogAction
          onClick={onConfirm}
          className="bg-red-900/50 text-red-200 hover:bg-red-900/70 border border-red-900"
          data-testid="confirm-delete-button"
        >
          Retirer
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// --- Main Dashboard ---

const Dashboard = () => {
  const { user, token, logout } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/packages`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setPackages(response.data);
    } catch (err) {
      const status = err.response?.status || "network";
      const detail = err.response?.data?.detail || err.message || "Erreur inconnue";
      console.error("fetchPackages error:", status, detail, err);
      toast.error(`Impossible de charger les forfaits (${status}: ${detail})`);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and axios are stable module-level constants
  }, [token]);

  useEffect(() => {
    fetchPackages();
    const interval = setInterval(fetchPackages, PRICE_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchPackages]);

  const handleAddPackage = async (e) => {
    e.preventDefault();
    
    if (!newUrl.trim()) {
      toast.error("Veuillez entrer une URL");
      return;
    }

    if (!newUrl.includes("vacations.aircanada.com") && !newUrl.includes("transat.com")) {
      toast.error("Veuillez entrer une URL valide d'Air Canada ou Air Transat");
      return;
    }

    setAdding(true);
    try {
      await axios.post(
        `${API}/packages`,
        { url: newUrl },
        { 
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Forfait ajoute! Recuperation du prix...");
      setNewUrl("");
      setAddModalOpen(false);
      fetchPackages();
    } catch (error) {
      const message = error.response?.data?.detail || "Impossible d'ajouter le forfait";
      toast.error(message);
    } finally {
      setAdding(false);
    }
  };

  const handleDeletePackage = async () => {
    if (!selectedPackage) return;

    try {
      await axios.delete(`${API}/packages/${selectedPackage.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success("Forfait retire du suivi");
      setPackages(packages.filter((p) => p.id !== selectedPackage.id));
    } catch {
      toast.error("Impossible de retirer le forfait");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedPackage(null);
    }
  };

  const handleRefresh = async (pkg) => {
    setRefreshingId(pkg.id);
    try {
      await axios.post(
        `${API}/packages/${pkg.id}/refresh`,
        {},
        { 
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Actualisation du prix...");
      setTimeout(fetchPackages, 5000);
    } catch {
      toast.error("Impossible d'actualiser le prix");
    } finally {
      setTimeout(() => setRefreshingId(null), 5000);
    }
  };

  const handleRequestDelete = (pkg) => {
    setSelectedPackage(pkg);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-black/30 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                <Plane className="w-5 h-5 text-white" />
              </div>
              <h1 className="font-heading text-lg font-semibold text-white hidden sm:block">
                Forfaits Vacances Tracker
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-white/50 hidden sm:block">
                {user?.username}
              </span>
              {user?.is_admin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSettings(true)}
                  className="text-white/50 hover:text-white hover:bg-white/5"
                  data-testid="settings-button"
                >
                  <SettingsIcon className="w-4 h-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-white/50 hover:text-white hover:bg-white/5"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Deconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {showSettings ? (
          <Settings onBack={() => setShowSettings(false)} />
        ) : (
        <>
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white tracking-tight">
              Forfaits Suivis
            </h2>
            <p className="text-white/50 text-sm mt-1">
              {packages.length} forfait{packages.length !== 1 ? "s" : ""} - Verifie 2x par jour (minuit et midi HNE)
            </p>
          </div>

          <Button
            onClick={() => setAddModalOpen(true)}
            className="bg-white text-black hover:bg-white/90 font-semibold h-10 px-4 transition-transform active:scale-95"
            data-testid="add-package-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un forfait
          </Button>
        </div>

        {/* Package List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : packages.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Plane className="w-8 h-8 text-white/30" />
            </div>
            <h3 className="font-heading text-lg font-medium text-white mb-2">
              Aucun forfait suivi
            </h3>
            <p className="text-white/50 text-sm mb-6 max-w-md mx-auto">
              Commencez par ajouter l'URL d'un forfait vacances pour suivre son prix.
            </p>
            <Button
              onClick={() => setAddModalOpen(true)}
              className="bg-white text-black hover:bg-white/90 font-semibold"
              data-testid="add-first-package-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter votre premier forfait
            </Button>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="packages-list">
            {packages.map((pkg, index) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                index={index}
                refreshingId={refreshingId}
                onRefresh={handleRefresh}
                onDelete={handleRequestDelete}
                token={token}
              />
            ))}
          </div>
        )}

        {/* How-to Guide - always shown below packages */}
        <HowToGuide />
        </>
        )}
      </main>

      <AddPackageModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        onSubmit={handleAddPackage}
        newUrl={newUrl}
        setNewUrl={setNewUrl}
        adding={adding}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        selectedPackage={selectedPackage}
        onConfirm={handleDeletePackage}
      />
    </div>
  );
};

export default Dashboard;
